import { Injectable } from '@angular/core'
import {
  HttpService,
  IExportFileResponse,
  INew_TableAction,
  ITimePeriodRequest,
  TableActionsService
} from '@lla-platform/core/core-data-access'
import { Observable } from 'rxjs'
import {
  IAttentionSummaryResponse,
  IEditLocationAttentionRequest,
  IShopLocationItem,
  IStatusBoardResponse
} from '../interfaces/shops.interface'
import { SHOPS_API_URL } from './shops-api-routes'
import {
  ICopyPreferredVendorsRequest,
  IPreferredVendorsLocationItem,
  IPreferredVendorsResponse,
  IUpdatePreferredVendorsRequest,
  IVendorResponse,
  IVendorsListResponse,
  IVendorDocumentDetailResponse,
  IVendorsSummaryByLevelResponse
} from '../interfaces/vendors.interface'
import { DateService } from '@lla-platform/core/core-util'
import { IRegionalManagersResponse } from '../interfaces/regional-managers.interface'
import { IDistrictManagersResponse } from '../interfaces/district-managers.interface'
import { ILocationGroupsResponse } from '../interfaces/location-groups.interface'

@Injectable({
  providedIn: 'root'
})
export class ShopsService {
  constructor(
    private http: HttpService,
    private dateService: DateService,
    private tableActionsService: TableActionsService
  ) {}

  getAllShops(): Observable<IShopLocationItem[]> {
    return this.http.get(SHOPS_API_URL.allShops)
  }

  getStatusBoard(): Observable<IStatusBoardResponse> {
    return this.http.get(SHOPS_API_URL.statusBoard)
  }

  getAttentionSummary(): Observable<IAttentionSummaryResponse> {
    return this.http.get(SHOPS_API_URL.attentionSummary)
  }

  editLocationAttention(locationId: string, payload: IEditLocationAttentionRequest): Observable<void> {
    return this.http.post(SHOPS_API_URL.editLocationAttention(locationId), payload)
  }

  getAllVendors(payload: ITimePeriodRequest): Observable<IVendorResponse> {
    const { from, to } = payload
    return this.http.post<IVendorResponse>(SHOPS_API_URL.allVendors, {
      period: payload.periodType,
      from: from ? this.dateService.momentToDateString(from) : undefined,
      to: to ? this.dateService.momentToDateString(to) : undefined
    })
  }

  getVendorsSummaryByLevel(payload: ITimePeriodRequest): Observable<IVendorsSummaryByLevelResponse> {
    const { from, to } = payload
    return this.http.post<IVendorsSummaryByLevelResponse>(SHOPS_API_URL.vendorsSummaryByLevel, {
      period: payload.periodType,
      from: from ? this.dateService.momentToDateString(from) : undefined,
      to: to ? this.dateService.momentToDateString(to) : undefined
    })
  }

  getPreferredVendors(): Observable<IPreferredVendorsResponse> {
    return this.http.get(SHOPS_API_URL.preferredVendors)
  }

  getPreferredVendorsDetail(locationId: string): Observable<IPreferredVendorsLocationItem> {
    return this.http.get(SHOPS_API_URL.preferredVendorsByLocation(locationId))
  }

  updatePreferredVendors(locationId: string, payload: IUpdatePreferredVendorsRequest): Observable<void> {
    return this.http.post(SHOPS_API_URL.preferredVendorsByLocation(locationId), payload)
  }

  copyPreferredVendors(locationId: string, payload: ICopyPreferredVendorsRequest): Observable<void> {
    return this.http.post(SHOPS_API_URL.copyPreferredVendors(locationId), payload)
  }

  getVendorsList(info: INew_TableAction): Observable<IVendorsListResponse> {
    return this.http.post(SHOPS_API_URL.vendorsList, this.tableActionsService.createRequestPayload(info))
  }

  exportVendorsList(info: INew_TableAction): Observable<IExportFileResponse> {
    return this.http.post(
      SHOPS_API_URL.exportVendorsList,
      this.tableActionsService.createRequestPayload(info)
    )
  }

  exportPreferredVendors(locationIds?: string[]): Observable<IExportFileResponse> {
    return this.http.post(SHOPS_API_URL.exportPreferredVendors, { locationIds })
  }

  getVendorDocument(vendorDocumentId: string): Observable<IVendorDocumentDetailResponse> {
    return this.http.get(SHOPS_API_URL.vendorDocument(vendorDocumentId))
  }

  getAllRegionalManagers(): Observable<IRegionalManagersResponse> {
    return this.http.get(SHOPS_API_URL.getAllRegionalManagers)
  }

  getAllDistrictManagers(): Observable<IDistrictManagersResponse> {
    return this.http.get(SHOPS_API_URL.getAllDistrictManagers)
  }

  getAllLocationGroups(): Observable<ILocationGroupsResponse> {
    return this.http.get(SHOPS_API_URL.getAllLocationGroups)
  }
}
